import type { WeaponSpec, WeaponTier, WeaponType } from './types';

// Every tuning value of the simulation (rule 6). Each value was settled in a tuning round listed in docs/PLAN.md.

/** Simulation rate: the only notion of time the simulation knows (rule 2). */
export const TICK_HZ = 60;
export const PHASE_TRANSITION_TICKS = 3 * TICK_HZ;
export const TRANSITION_STYLE = { color: '#ffffff', font: 'bold 28px sans-serif', lineHeight: 44 } as const;

/** World units per tile. */
export const TILE = 32;

// --- bodies -----------------------------------------------------------------
export const PLAYER_RADIUS = 12;
export const PLAYER_MASS = 1;
/** velocity (world units / s) added per tick while the stick is fully pushed */
export const PLAYER_ACCEL = 40;
/** velocity multiplier per tick */
export const PLAYER_FRICTION = 0.85;
/** world units / s */
export const PLAYER_MAX_SPEED = 220;

export const CRATE_RADIUS = 14;
export const CRATE_MASS = 2.5;
export const CRATE_FRICTION = 0.9;

/** below this speed (world units / s) a body comes to rest */
export const REST_SPEED = 2;
/** bounciness of body-body bumps: 0 = they stick, 1 = elastic */
export const RESTITUTION = 0.2;
/** separation passes per tick so chains of touching bodies settle */
export const SEPARATION_ITERATIONS = 2;

// --- arena ------------------------------------------------------------------
/** radius of a large round pillar ('O' in the level art), world units */
export const PILLAR_RADIUS_LARGE = 1.5 * TILE;
/** radius of a small round pillar ('o') */
export const PILLAR_RADIUS_SMALL = 0.5 * TILE;
/** crates scattered at random when a match starts, as point-symmetric pairs so the layout stays fair */
export const ARENA_CRATE_PAIRS = 4;
/** smallest gap between a scattered crate's rim and walls, pillars or other crates: a player always fits through */
export const CRATE_SCATTER_GAP = 2 * PLAYER_RADIUS + 4;
/** scattered crates keep at least this distance (centre to centre) from the starts, so nobody spawns boxed in. */
export const CRATE_SCATTER_START_CLEARANCE = 3 * TILE;
/** random draws per crate pair before the pair is skipped (bounded, hence deterministic) */
export const CRATE_SCATTER_ATTEMPTS = 40;

// --- NPC boxes --------------------------------------------------------------
export const NPC_BOX_FIRST_TICKS = 0;
export const NPC_BOX_INTERVAL_TICKS = Math.round(2 * TICK_HZ / 0.7);
export const NPC_BOX_MAX = 7;
export const NPC_BOX_TIER_WEIGHT = [3, 2, 1] as const;
export const NPC_RADIUS = 10;
export const NPC_MASS = 0.8;
export const NPC_MAX_SPEED = 130;
export const NPC_ACCEL = PLAYER_ACCEL;
export const NPC_FRICTION = PLAYER_FRICTION;
export const NPC_REPLAN_TICKS = 20;
export const NPC_PREFERRED_DIST = 5 * TILE;
export const NPC_RETREAT_DIST = 0.8 * NPC_PREFERRED_DIST;
export const NPC_STRAFE = 0.4;
export const NPC_FIRE_RANGE = 9 * TILE;
export const NPC_FIRE_TICKS = { 2: Math.round(2.5 * TICK_HZ), 3: Math.round(1.2 * TICK_HZ) } as const;
/** Maximum deviation to either side of the led aim. */
export const NPC_AIM_JITTER_DEG = 6;
export const NPC_HIT_PENALTY_MS = 2_000;
export const NPC_TOUCH_PENALTY_MS = 3_000;
export const NPC_BOX_RADIUS = CRATE_RADIUS;
/** Minimum centre distance from bodies, starts, pickups and pending pickups. */
export const NPC_BOX_CLEARANCE = 2 * TILE;

// --- match ------------------------------------------------------------------
/** each player's chess clock at the start; the only resource of the match */
export const START_CHESS_CLOCK_MS = 5 * 60 * 1000;
/** a hit in the arena costs the victim this much chess time, the same for every weapon */
export const HIT_PENALTY_MS = 5_000;
/**
 * The match alternates arena and board: these are the phase lengths. The arena is kept shorter than the board
 * so that a skirmish stays a skirmish and the clock is mostly decided by thinking (chess tuning round 2026-10-25).
 */
export const SHOOTER_PHASE_TICKS = 45 * TICK_HZ;
export const CHESS_PHASE_TICKS = 60 * TICK_HZ;
/** what one tick at the board takes off the clock of the player to move */
export const CHESS_TICK_MS = 1000 / TICK_HZ;
/** the client counts down the last seconds of a phase ("Schach in 3 …"); presentation only */
export const PHASE_WARNING_TICKS = 3 * TICK_HZ;

// --- weapons ----------------------------------------------------------------
/** half-angle: an aim within this many degrees of an opponent snaps onto them */
export const AIM_ASSIST_DEG = 8;
/** distance (centre to centre) at which a player collects a pickup */
export const PICKUP_RADIUS = 20;
/** Maximum pickups present at match start. */
export const INITIAL_PICKUPS = 8;
/** Minimum separation of pickups and pending replacements. */
export const PICKUP_SPAWN_CLEARANCE = 2 * PICKUP_RADIUS;
export const PICKUP_SPAWN_ATTEMPTS = 80;
/** Relative probabilities of weak, average and strong weapons in each zone. */
export const WEAPON_ZONE_WEIGHTS: Record<WeaponTier, readonly [number, number, number]> = {
  1: [70, 25, 5],
  2: [20, 60, 20],
  3: [5, 25, 70],
};
/** ticks until a collected pickup is replaced somewhere else */
export const RESPAWN_DELAY_TICKS = 2 * TICK_HZ;
/** a crate hit by a projectile receives this fraction of the weapon's knockback as impulse */
export const CRATE_PUSH_FACTOR = 0.6;
/** after a damaging hit, further hits within this window only knock back (one shotgun blast = one hit) */
export const HIT_INVULNERABLE_TICKS = Math.round(0.25 * TICK_HZ);
/** a projectile starts this far outside the shooter's circle so that it never spawns inside a wall it faces */
export const MUZZLE_GAP = 1;
/** how far (in player diameters) a shot slides the shooter back when nothing is in the way; the same for every weapon for now */
export const RECOIL_DIAMETERS = 3;
/**
 * The impulse that coasts a player exactly RECOIL_DIAMETERS: the shot adds v0 after this tick's friction,
 * so the body travels v0/TICK_HZ * (1 + f + f^2 + ...) = v0 / (TICK_HZ * (1 - f)).
 */
export const RECOIL = RECOIL_DIAMETERS * 2 * PLAYER_RADIUS * TICK_HZ * (1 - PLAYER_FRICTION) * PLAYER_MASS;

/** ticks an explosion stays in the state after it has dealt its damage; rendering only */
export const BLAST_TTL_TICKS = Math.round(0.3 * TICK_HZ);
/**
 * A curving round is aimed so that its arc passes through the aim point; an aim point farther away
 * than this is pulled in to this distance, so a shot "into the distance" still leaves at a sane
 * angle instead of sideways (the arc then runs through the point this far along the aim).
 */
export const CURVE_MAX_CHORD = 7 * TILE;


/** a plain one-round weapon; the entries below override what differs */
const SINGLE_SHOT = {
  kind: 'projectile',
  pattern: 'spread',
  pellets: 1,
  spreadDeg: 0,
  bounces: 0,
  burst: 1,
  burstIntervalTicks: 0,
  curveDegPerSec: 0,
  blastRadius: 0,
} as const;

/**
 * The weapon table, ordered by tier. A pickup holds exactly one trigger pull. Every hit costs
 * HIT_PENALTY_MS; the tiers differ in how easily they land one.
 *   tier 1 (weak):    pistol, curve (bends around cover)
 *   tier 2 (average): shotgun (close range), burst (three rounds; the second lands in the invulnerability
 *                     window, so a full burst is two hits), hexa (six directions at once), cannon (big, bounces)
 *   tier 3 (strong):  flame (short beam that grows and lingers), laser (instant beam, through crates, whole
 *                     arena), rocket (explodes, shoves everyone around)
 */
export const WEAPONS: Record<WeaponType, WeaponSpec> = {
  pistol: {
    ...SINGLE_SHOT,
    tier: 1,
    projectileSpeed: 640,
    projectileRadius: 3,
    ttlTicks: 2 * TICK_HZ,
    recoil: RECOIL,
    knockback: 250,
  },
  curve: {
    ...SINGLE_SHOT,
    tier: 1,
    projectileSpeed: 420,
    projectileRadius: 6,
    ttlTicks: Math.round(1.5 * TICK_HZ),
    recoil: RECOIL,
    knockback: 400,
    curveDegPerSec: 150,
  },
  shotgun: {
    ...SINGLE_SHOT,
    tier: 2,
    projectileSpeed: 520,
    projectileRadius: 2.5,
    ttlTicks: Math.round(0.35 * TICK_HZ),
    recoil: RECOIL,
    knockback: 120,
    pellets: 5,
    spreadDeg: 30,
  },
  burst: {
    ...SINGLE_SHOT,
    tier: 2,
    projectileSpeed: 600,
    projectileRadius: 3,
    ttlTicks: 2 * TICK_HZ,
    recoil: RECOIL,
    knockback: 200,
    burst: 3,
    burstIntervalTicks: 9,
  },
  hexa: {
    ...SINGLE_SHOT,
    tier: 2,
    projectileSpeed: 480,
    projectileRadius: 3,
    ttlTicks: Math.round(1.5 * TICK_HZ),
    recoil: RECOIL,
    knockback: 250,
    pellets: 6,
    pattern: 'ring',
  },
  cannon: {
    ...SINGLE_SHOT,
    tier: 2,
    projectileSpeed: 540,
    projectileRadius: 7,
    ttlTicks: 3 * TICK_HZ,
    recoil: RECOIL,
    knockback: 600,
    bounces: 1,
  },
  flame: {
    kind: 'beam',
    tier: 3,
    range: 5 * TILE,
    width: 10,
    growTicks: Math.round(0.4 * TICK_HZ),
    ttlTicks: Math.round(1.2 * TICK_HZ),
    recoil: RECOIL,
    knockback: 150,
  },
  laser: {
    kind: 'beam',
    tier: 3,
    range: 40 * TILE,
    width: 2,
    growTicks: 0,
    ttlTicks: Math.round(0.25 * TICK_HZ),
    recoil: RECOIL,
    knockback: 200,
  },
  rocket: {
    ...SINGLE_SHOT,
    tier: 3,
    projectileSpeed: 300,
    projectileRadius: 6,
    ttlTicks: 2 * TICK_HZ,
    recoil: RECOIL,
    knockback: 500,
    blastRadius: 2 * TILE,
  },
};

/** every weapon, in the order of the table above (weak to strong) */
export const WEAPON_TYPES: readonly WeaponType[] = ['pistol', 'curve', 'shotgun', 'burst', 'hexa', 'cannon', 'flame', 'laser', 'rocket'];

// --- bot --------------------------------------------------------------------
/** the bot re-rolls its random choices (strafe side, dodge) every this many ticks */
export const BOT_DECISION_TICKS = 20;
/** it only shoots within this distance and with line of sight */
export const BOT_FIRE_RANGE = 10 * TILE;
/** per-tick chance to pull the trigger once in range: emulates human reaction delay */
export const BOT_FIRE_CHANCE = 0.06;
/** armed, it closes in to about this distance and then circles */
export const BOT_PREFERRED_DIST = 4 * TILE;
/** sideways component while approaching (0 = straight in, 1 = pure circling) */
export const BOT_STRAFE = 0.6;
/** chance per decision slot to dodge sideways instead of pursuing */
export const BOT_DODGE_CHANCE = 0.15;
/** how far ahead of a moving target the bot aims (1 = full lead, 0 = none) */
export const BOT_LEAD = 1;

// --- chess bot --------------------------------------------------------------
/**
 * The bot moves this long after the previous move (or the start of the chess phase). The pause is paid from its
 * clock: at 1 s the bot spent a fraction of what a human spends per move and the clock race was lopsided, so
 * 3 s brings its clock use closer to a brisk human's (chess tuning round 2026-10-25).
 */
export const BOT_CHESS_THINK_TICKS = 3 * TICK_HZ;
/** full-width search depth in plies; see docs/SCHACH.md for the measured cost per depth */
export const BOT_CHESS_DEPTH = 2;
/** captures and promotions are followed this many plies past the horizon so that an exchange is counted to its end */
export const BOT_CHESS_QUIESCENCE_PLIES = 4;
/** Choose among moves within this many centipawns of the best; forced mates stay exact. */
export const BOT_CHESS_VARIETY_CP = 20;

// --- presentation (data only; never changes simulation or collision sizes) -----
/** CSS pixels; portrait keeps full thumb targets. */
export const PAD_RADIUS = 56;
export const PAD_PORTRAIT_SCALE = 1;
export const CAMERA_STYLE = {
  targetPlayerDiameter: 30,
  overviewMinPlayerDiameter: 24,
  minVisibleTiles: 9,
  margin: 0,
  hudHeight: 88,
  hudCompactHeight: 64,
  maxDpr: 2,
  minimapWidth: 104,
  minimapMargin: 12,
  minimapPadding: 6,
  minimapDot: 3,
} as const;

export const ARENA_ART = {
  textureScale: 2,
  outline: 2,
  seam: 1,
  shadowX: 3,
  shadowY: 5,
  wallInset: 2,
  wallBevel: 4,
  pillarRim: 5,
  pillarInset: 0.77,
  emblemScale: 0.65,
  crateInset: 4,
  crateCorner: 3,
  crateBrace: 3,
  teamColors: { me: '#39c9b4', meLight: '#a4f3cf', foe: '#ef806b', foeLight: '#ffd0a3' },
  tierColors: { 1: '#e8e8e8', 2: '#f0c040', 3: '#ff5e7a' },
  npcBox: {
    rim: 3,
    face: '#263e3a',
    notchEmpty: '#697e78',
    emblemY: -3,
    emblemScale: 0.8,
    notchY: 7,
    notchWidth: 3,
    notchHeight: 4,
    notchGap: 2,
    minimapDot: 2,
  },
  npc: {
    handX: 0.7,
    handY: 0.7,
    handRadius: 0.23,
    highlight: 0.65,
    visorX: 0.15,
    visorWidth: 0.65,
    visorHeight: 0.8,
    knifeGripLength: 4,
    knifeGripWidth: 3,
    knifeBladeLength: 8,
    knifeBladeWidth: 5,
    bladeColor: '#f3f0da',
    pistol: { length: 8, width: 3 },
    rifle: { length: 16, width: 3 },
    rifleStockLength: 7,
    rifleStockWidth: 6,
    rifleStockColor: '#b18a58',
    barrelColor: '#d7ddc7',
    minimapDot: 2,
  },
  pickupRadius: 16,
  pickupPulse: 0.045,
  pickupPulseTicks: 15,
  pickupHalo: 5,
  pickupLabelY: 26,
  pickupLabelFont: '600 9px system-ui, sans-serif',
  weaponScale: 0.8,
  weaponLength: 20,
  weaponWidth: 7,
  weaponGrip: 8,
  weaponTip: 4,
  weaponStock: 5,
  weaponBarrel: 3,
  weaponHeavyWidth: 10,
  weaponRingRadius: 6,
  weaponCoreRadius: 2,
  playerRingGap: 3,
  playerRingWidth: 1.5,
  playerHighlight: 0.7,
  playerVisorWidth: 0.7,
  playerVisorHeight: 1.1,
  playerVisorX: 0.15,
  playerHandX: 0.65,
  playerHandY: 0.72,
  playerHandRadius: 0.27,
  playerWeaponOffset: 0.95,
  playerLabelY: 26,
  playerLabelFont: '700 9px system-ui, sans-serif',
  trailTicks: 0.025,
  trailAlpha: 0.32,
  projectileCore: 0.45,
} as const;

/** Confirmed hit ring, in ticks and world units. */
export const HIT_FEEDBACK_STYLE = { ticks: 36, gap: 6, growth: 30, width: 5, color: '#ffe0a3' } as const;

/** Personal board drawings, adapted from Rogess; lengths are fractions of a square. */
export const BOARD_DRAWING = {
  circleRadius: 0.44, circleWidth: 0.065, shaftWidth: 0.14,
  headLength: 0.3, headHalfWidth: 0.22, headOutline: 0.035,
  tipInset: 0.08, sharedTipInset: 0.3, sharedLaneGap: 0.16,
  color: '#4d913e', outline: '#e6edc5', opacity: 0.85,
} as const;

/** Rogess board mix and its soft, synthesized selection click. */
export const CHESS_AUDIO = {
  maxLateMs: 450,
  volume: { select: 0.4, move: 0.6, capture: 1, check: 1, promotion: 1 },
  select: { hz: 430, gain: 0.13, duration: 0.08, attack: 0.003, decay: 5, release: 0.025,
    partials: [[2.76, 0.28], [5.4, 0.08]] },
} as const;
